import Image from "next/image"
import { Button, Card, Stack, Typography } from "@octopus-energy/coral/atoms"
import { useIsOpen } from "@oefr/hooks"
import { useTranslations } from "next-intl"
import { apiRoutesPaths } from "@/routes/api"
import { Partner } from "../../constants/partner"
import { type DartySession } from "../../session/darty"
import { DartyStoreDialog } from "./DartyStoreDialog"

type DartyEmployeeCardProps = {
    session?: DartySession
}

export function DartyEmployeeCard({ session }: DartyEmployeeCardProps) {
    const t = useTranslations("DartyEmployeeCard")
    const { isOpen, open, close } = useIsOpen()

    if (!session) return null

    const hasStore = !!session.affiliateQueryParams?.store

    return (
        <>
            <Card fullWidth>
                <Stack
                    gap="xxl"
                    flexDirection={{ base: "column", sm: "row" }}
                >
                    <Stack justifyContent={"center"}>
                        <Image
                            src="/assets/images/illustrations/constantine-glasses.svg"
                            alt={t("imageAlt")}
                            width={124}
                            height={96}
                        />
                    </Stack>
                    <Stack
                        flexDirection="column"
                        alignItems="start"
                    >
                        <Typography textStyle="h3">
                            {t.rich("greeting", {
                                b: (chunks) => (
                                    <Typography
                                        component="b"
                                        textStyle="h3"
                                        color="tertiary500"
                                    >
                                        {chunks}
                                    </Typography>
                                ),
                                name: session.user.fullname,
                            })}
                        </Typography>

                        <Typography>
                            {t("label.employee", { employeeNumber: session.user.uid })}
                        </Typography>
                        <Typography>
                            {t("label.store", {
                                storeName: session.affiliateQueryParams?.store?.name ?? "...",
                            })}
                        </Typography>

                        <Stack flexDirection={{ base: "column", md: "row" }}>
                            <Button
                                size="small"
                                color="primary500"
                                onClick={open}
                            >
                                {t("callToAction.updateStore")}
                            </Button>
                            <Button
                                size="small"
                                color="secondary500"
                                href={apiRoutesPaths.partnerAuthLogout.resolve({
                                    pathParams: {
                                        partnerSlug: Partner.Darty,
                                    },
                                })}
                            >
                                {t("callToAction.logout")}
                            </Button>
                        </Stack>
                    </Stack>
                </Stack>
            </Card>

            <DartyStoreDialog
                isOpen={isOpen || !hasStore}
                onClose={hasStore ? close : undefined}
                session={session}
            />
        </>
    )
}

DartyEmployeeCard.messages = ["DartyEmployeeCard", ...DartyStoreDialog.messages] as const
